import { useSyncExternalStore } from 'react';
import type { BrandConfig } from './types';
import { argdiamond } from './configs/argdiamond';
import { royalx } from './configs/royalx';
import { markBrandResolved, setSiteSettings } from './settings';
import { setBrandMenus } from './menus';
import { setBrandFeatures } from './features';

export type { BrandConfig } from './types';
export * from './settings';
export * from './menus';
export * from './menuRoutes';
export * from './features';
export * from './featureRoutes';

/** All available brands, keyed by id. Naya brand yahan register karo. */
const BRANDS: Record<string, BrandConfig> = {
  argdiamond,
  royalx,
};

/** Which brand is the default when VITE_BRAND is not set. */
const DEFAULT_BRAND = 'argdiamond';

/**
 * The active brand for this build.
 * Selected at build time via `VITE_BRAND=royalx npm run build`.
 */
function resolveBrand(): BrandConfig {
  const key = (import.meta.env.VITE_BRAND as string | undefined) ?? DEFAULT_BRAND;
  return BRANDS[key] ?? BRANDS[DEFAULT_BRAND] ?? argdiamond;
}

export const brand: BrandConfig = resolveBrand();

/**
 * The hostname the backend resolves the brand from (sent as `X-Brand-Domain`).
 *
 * Normally the browser's own host. In local dev that is `localhost`, which maps
 * to no brand at all — so the theme, the feature flags and the Site Settings all
 * come back empty and none of them can be tested. Setting VITE_BRAND_DOMAIN in
 * `.env.local` points dev at a real brand without editing /etc/hosts or the
 * database. Ignored outside dev, so a stray env var can never re-point a
 * production build at someone else's brand.
 */
export function brandDomain(): string {
  const override = import.meta.env.VITE_BRAND_DOMAIN as string | undefined;
  if (import.meta.env.DEV && override?.trim()) return override.trim();
  return window.location.hostname;
}

// --- Reactive brand logo ----------------------------------------------------
// Components render the logo from the build-time config, but the super admin may
// have uploaded a per-brand logo (brand.logoUrl from GET /brand). We keep it in
// a tiny external store so every <img> updates the instant /brand resolves —
// without blocking first paint. Use `useBrandLogo()` instead of
// `brand.assets.logo` wherever the logo is shown.
let logoOverride: string | null = null;
const logoSubs = new Set<() => void>();

export function useBrandLogo(): string {
  return useSyncExternalStore(
    (cb) => {
      logoSubs.add(cb);
      return () => { logoSubs.delete(cb); };
    },
    () => logoOverride ?? brand.assets.logo,
  );
}

function setBrandLogo(url: string): void {
  if (!url || url === logoOverride) return;
  // The super-admin upload can 404 — the file gets deleted from storage, or the
  // brand was saved with a URL that never resolved. Adopting it blindly swaps a
  // working built-in logo for a broken-image icon on every screen, so probe it
  // first and keep the fallback if it does not load.
  const probe = new Image();
  probe.onload = () => {
    logoOverride = url;
    logoSubs.forEach((f) => f());
  };
  probe.src = url;
}

/**
 * Inject the active brand's colors as CSS variables on <html>, and set the
 * document title / favicon. Call once at app startup (before render) so the
 * hardcoded values in tailwind.config.js resolve to this brand.
 */
/**
 * Deposit / Withdraw / FD button gradients. Superadmin overrides these per brand
 * via the brand's `theme.buttons` blob; this is the fallback default.
 */
export interface ActionButtonColors {
  depositFrom?: string;
  depositTo?: string;
  withdrawFrom?: string;
  withdrawTo?: string;
  fdFrom?: string;
  fdTo?: string;
}

/**
 * Crorebet-style defaults: money-in is the green CTA, the rest are ink plates.
 * Each pair is the same colour twice — the reference paints flat fills, not
 * gradients, and the two stops are what `linear-gradient` consumers expect.
 * A brand that sets its own colours in the panel still overrides these.
 */
export const DEFAULT_ACTION_BUTTONS: Required<ActionButtonColors> = {
  depositFrom: '#16b14a', depositTo: '#16b14a',
  withdrawFrom: '#343131', withdrawTo: '#343131',
  fdFrom: '#343131', fdTo: '#343131',
};

const ACTION_BUTTON_VARS: Record<keyof ActionButtonColors, string> = {
  depositFrom: '--btn-dep-from',
  depositTo: '--btn-dep-to',
  withdrawFrom: '--btn-wd-from',
  withdrawTo: '--btn-wd-to',
  fdFrom: '--btn-fd-from',
  fdTo: '--btn-fd-to',
};

/** Write the six action-button gradient stops onto <html>. */
export function applyActionButtonColors(buttons?: ActionButtonColors | null): void {
  const root = document.documentElement;
  const merged = { ...DEFAULT_ACTION_BUTTONS, ...(buttons ?? {}) };
  (Object.keys(ACTION_BUTTON_VARS) as (keyof ActionButtonColors)[]).forEach((key) => {
    const value = merged[key];
    if (typeof value === 'string' && value.trim()) {
      root.style.setProperty(ACTION_BUTTON_VARS[key], value.trim());
    }
  });
}

export function applyBrandTheme(config: BrandConfig = brand): void {
  const root = document.documentElement;
  const c = config.colors;

  // Deposit / Withdraw / FD defaults — overridden later by the remote brand.
  applyActionButtonColors();

  // shadcn primary (HSL triple)
  root.style.setProperty('--primary', c.primaryHsl);
  root.style.setProperty('--ring', c.primaryHsl);

  // brand-specific values that tailwind gradients now reference
  root.style.setProperty('--brand-primary', c.primary);
  root.style.setProperty('--brand-header-from', c.headerGradient[0]);
  root.style.setProperty('--brand-header-to', c.headerGradient[1]);
  root.style.setProperty('--brand-btn-from', c.buttonGradient[0]);
  root.style.setProperty('--brand-btn-to', c.buttonGradient[1]);

  // title
  document.title = config.title;

  // favicon(s) — swap every declared icon link to the brand favicon
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="icon"]')
    .forEach((link) => {
      link.href = config.assets.favicon;
    });

  // apple touch icon (iOS add-to-homescreen)
  const appleIcon = document.querySelector<HTMLLinkElement>(
    'link[rel="apple-touch-icon"]'
  );
  if (appleIcon) appleIcon.href = config.assets.appleTouchIcon;

  // browser theme-color (address bar / PWA)
  let themeMeta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]'
  );
  if (!themeMeta) {
    themeMeta = document.createElement('meta');
    themeMeta.name = 'theme-color';
    document.head.appendChild(themeMeta);
  }
  themeMeta.content = c.primary;
}

interface RemoteBrand {
  resolved?: boolean;
  brandName?: string;
  title?: string;
  primaryColor?: string;
  primaryHsl?: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  theme?: { buttons?: ActionButtonColors } & Record<string, unknown>;
  features?: unknown;
  /** Site Settings, pre-merged over the defaults by the server. */
  settings?: Record<string, boolean>;
  /** Menu Visibility, pre-merged over the defaults by the server. */
  menus?: Record<string, Record<string, boolean>>;
}

declare global {
  interface Window {
    __BRAND__?: RemoteBrand;
  }
}

/**
 * Paint the build-time brand immediately, then fetch the Host-resolved brand from
 * the server (`GET /brand`) and override it with whatever superadmin configured —
 * including the Deposit / Withdraw / FD button colours.
 */
export async function applyRemoteBrandTheme(): Promise<RemoteBrand | null> {
  applyBrandTheme();

  const apiBase = ((import.meta.env.VITE_API_BASE_URL as string) || '/api').replace(/\/$/, '');
  try {
    const res = await fetch(`${apiBase}/brand`, {
      credentials: 'include',
      headers: { 'X-Brand-Domain': brandDomain() },
    });
    if (!res.ok) return null;
    const b = (await res.json()) as RemoteBrand;
    // Site Settings apply even to an unresolved brand — the server still sends
    // the platform defaults there, and reading them keeps one code path.
    setSiteSettings(b?.settings ?? (b?.theme?.settings as unknown));
    // Same reasoning as the settings above: an unresolved brand still gets the
    // platform defaults from the server, so one code path covers both.
    setBrandMenus(b?.menus ?? (b?.theme?.menus as unknown));
    // Features too: an unresolved brand has none, and the store's all-on default
    // is exactly what the backend guards allow in that case, so one code path
    // covers both rather than leaving the products stuck on a stale brand's map.
    setBrandFeatures(b?.features);
    if (!b || !b.resolved) return b ?? null;

    // Dev escape hatch: identify as the brand (so the brand-scoped login and
    // signup checks run against the right one) while keeping the look this build
    // ships with. Needed because the brand used for local testing is not themed,
    // so adopting its colours repainted the whole site for no reason. Everything
    // above this line — settings, menus, features — still applies, because those
    // decide behaviour rather than appearance.
    if (import.meta.env.DEV && import.meta.env.VITE_KEEP_BUILD_THEME === 'true') {
      window.__BRAND__ = b;
      return b;
    }

    const root = document.documentElement;
    if (b.primaryHsl) {
      root.style.setProperty('--primary', b.primaryHsl);
      root.style.setProperty('--ring', b.primaryHsl);
    }
    if (b.primaryColor) root.style.setProperty('--brand-primary', b.primaryColor);
    if (b.title) document.title = b.title;
    if (b.faviconUrl) {
      document
        .querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]')
        .forEach((link) => { link.href = b.faviconUrl as string; });
    }

    applyActionButtonColors(b.theme?.buttons);

    if (b.logoUrl) setBrandLogo(b.logoUrl);

    window.__BRAND__ = b;
    return b;
  } catch {
    return null;
  } finally {
    // Always — a brand lookup that failed must not hold the splash up forever.
    markBrandResolved();
  }
}
